import React, { useState } from "react";
import {
  Link,
  createFileRoute,
  redirect,
  stripSearchParams,
} from "@tanstack/react-router";
import * as z from "zod";
import { zodValidator } from "@tanstack/zod-adapter";
import {
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Calendar, Copy, Search, Volume2, X } from "lucide-react";
import { HTTPError } from "ky";
import { toast } from "sonner";
import { addMilliseconds } from "date-fns/addMilliseconds";
import { addSeconds } from "date-fns/addSeconds";
import * as duration from "duration-fns";
import { MeetingsPagination } from "../meetings/-components/MeetingsPagination";
import {
  formatMeetingDuration,
  formatPlatform,
} from "../meetings/-components/meetings-utils";
import { AlertActionsMenu } from "./-components/AlertActionsMenu";
import { AlertsFilterPopover } from "./-components/AlertsFilterPopover";
import {
  ALERT_DATE_FILTERS,
  ALERT_STATE_OPTIONS,
  ALERT_TYPE_OPTIONS,
  formatAlertDate,
  formatAlertTypeBadge,
  getAlertDateRange,
  getAlertEntities,
  getAlertThreats,
  getAlertTypeBadgeClasses,
  // getAvatarColor,
  // getInitials,
  matchesAlertState,
  matchesAlertType,
} from "./-components/alerts-utils";
import type {
  AlertStateFilter,
  AlertTypeFilter,
} from "./-components/alerts-utils";
import type {
  AlertDetail,
  AlertItem,
  UpdateAlertPayload,
} from "@/lib/queries/alerts";
import type { Utterance } from "../meetings/-hooks";
// import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { PII_BADGE_CLASSES } from "@/lib/pii-badge";
import { LoadingSpinner } from "@/components/loading";
import { ALERT_STATE, alertsListQueryOptions } from "@/lib/queries/alerts";
import { userInfoQueryOptions } from "@/lib/queries/auth";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ConfirmationDialog,
  ConfirmationDialogWarningIcon,
} from "@/components/ui/confirmation-dialog";
import { api } from "@/lib/http";
import { utteranceDetailsQueryOptions } from "@/lib/queries/utterances";
import {
  UtteranceAnalysisTabBar,
  UtteranceAnalysisTabContent,
  useUtteranceAnalysisTabs,
} from "@/components/utterances";
import { meetingBotQueryOptions } from "@/lib/queries/meetings";

function toApiFilterValue<T extends string>(
  filters: Array<T>,
  allValue: T,
): T | undefined {
  return filters.length === 1 && !filters.includes(allValue)
    ? filters[0]
    : undefined;
}

const searchSchema = z.object({
  page: z.number().int().positive().default(1).catch(1),
  per_page: z.number().int().positive().max(100).default(10),
  state: z.array(z.enum(ALERT_STATE_OPTIONS)).default(["All"]).catch(["All"]),
  alert_type: z
    .array(z.enum(ALERT_TYPE_OPTIONS))
    .default(["all"])
    .catch(["all"]),
  search: z.string().optional(),
  date_filter: z.string().optional(),
});

export const Route = createFileRoute("/_app/live-monitors-alerts/")({
  component: RouteComponent,
  validateSearch: zodValidator(searchSchema),
  search: {
    middlewares: [
      stripSearchParams({
        search: "",
      }),
    ],
  },
  loaderDeps: ({ search }) => search,
  async beforeLoad({ context: { queryClient } }) {
    const userInfo = await queryClient.ensureQueryData(userInfoQueryOptions);
    if (!userInfo.org_admin) throw redirect({ to: "/" });
  },
  loader({ context: { queryClient }, deps }) {
    queryClient.ensureQueryData(
      alertsListQueryOptions({
        ...deps,
        ...getAlertDateRange(deps.date_filter),
        state: toApiFilterValue(deps.state, "All"),
        alert_type: toApiFilterValue(deps.alert_type, "all"),
      }),
    );
  },
});

const tableHeadClass =
  "px-4 py-3 text-left text-sm font-medium text-[#717680] dark:text-muted-foreground md:px-6";
const tableCellClass = "px-4 py-4 md:px-6";

function SearchHighlighter({
  text,
}: {
  text: string;
  searchQuery: string;
}) {
  return <>{text}</>;

  // Sari rengle highlight funksiyasi deaktiv edilib
  // if (!searchQuery) return <>{text}</>;
  //
  // const index = text.toLowerCase().indexOf(searchQuery.toLowerCase());
  // if (index === -1) return <>{text}</>;
  //
  // return (
  //   <>
  //     {text.slice(0, index)}
  //     <span className="rounded bg-yellow-200 text-yellow-900 dark:bg-yellow-500/40 dark:text-yellow-100">
  //       {text.slice(index, index + searchQuery.length)}
  //     </span>
  //     {text.slice(index + searchQuery.length)}
  //   </>
  // );
}

function MeetingCell({
  alert,
  searchQuery,
}: {
  alert: AlertItem;
  searchQuery: string;
}) {
  const meeting = useQuery(meetingBotQueryOptions(alert.meeting.id));
  const subtitle = meeting.data
    ? [formatPlatform(meeting.data.platform), formatMeetingDuration(meeting.data)]
        .filter(Boolean)
        .join(" • ")
    : null;

  return (
    <TableCell className="relative p-0">
      <Link
        to="/meetings/$meetingId"
        params={{ meetingId: alert.meeting.id }}
        className="group/meeting-link flex min-h-[72px] items-center px-4 py-4 transition-colors hover:bg-muted/50 md:px-6"
      >
        <div className="min-w-0 flex-1">
          <p
            className="truncate text-sm font-medium text-[#181D27] dark:text-foreground"
            title={alert.meeting.name}
          >
            <SearchHighlighter
              text={alert.meeting.name}
              searchQuery={searchQuery}
            />
          </p>
          {meeting.isPending ? (
            <Skeleton className="mt-1 h-3 w-24" />
          ) : (
            <p
              className="mt-0.5 truncate text-xs text-[#535862] dark:text-muted-foreground"
              title={subtitle ?? ""}
            >
              {subtitle ?? "--"}
            </p>
          )}
        </div>
      </Link>
    </TableCell>
  );
}

function AlertsDateFilterButton({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="h-10 w-44 justify-start gap-2 px-3.5 text-sm font-medium text-[#414651] dark:text-foreground"
        >
          <Calendar className="size-4 shrink-0 text-muted-foreground" />
          <span className="truncate">
            {value === "all"
              ? "Select range"
              : (ALERT_DATE_FILTERS.find((f) => f.value === value)?.label ??
                "Select range")}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-48 p-1">
        {ALERT_DATE_FILTERS.map((filter) => (
          <button
            key={filter.value}
            type="button"
            onClick={() => {
              onChange(filter.value);
              setOpen(false);
            }}
            className={cn(
              "flex w-full rounded-md px-3 py-2 text-left text-sm transition-colors hover:bg-muted/50",
              value === filter.value && "bg-muted font-medium text-foreground",
            )}
          >
            {filter.label}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

function ParticipantCell({
  participant,
  searchQuery,
}: {
  participant: AlertItem["participant"];
  searchQuery: string;
}) {
  const participantName = participant?.name ?? "—";

  return (
    <div className="flex min-h-[72px] w-full items-center px-4 py-4 md:px-6">
      <span
        className="truncate text-sm font-medium text-[#181D27] dark:text-foreground"
        title={participant?.name}
      >
        <SearchHighlighter text={participantName} searchQuery={searchQuery} />
      </span>
    </div>
  );
}

function ThreatsCell({ alert }: { alert: AlertItem }) {
  const threats = getAlertThreats(alert);

  if (threats.length === 0) {
    return (
      <Badge
        variant="outline"
        className="rounded-full border-gray-200 bg-gray-50 px-3 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 dark:border-border dark:bg-muted/40 dark:text-muted-foreground dark:hover:bg-muted/40"
      >
        No threats
      </Badge>
    );
  }

  return (
    <div className="flex flex-wrap gap-1">
      {threats.map((threat) => (
        <Badge
          key={threat.kind}
          variant="outline"
          className={cn(
            "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
            threat.kind === "pii"
              ? PII_BADGE_CLASSES
              : "border-[#FECDCA] bg-[#FEF3F2] text-[#D92D20] dark:border-red-400/30 dark:bg-red-500/15 dark:text-red-300",
          )}
        >
          {threat.kind === "voice" && <Volume2 className="h-3 w-3" />}
          {threat.label}
        </Badge>
      ))}
    </div>
  );
}

function EntitiesCell({ alert }: { alert: AlertItem }) {
  const entities = getAlertEntities(alert);

  if (entities.length === 0) {
    return <span className="text-sm font-medium text-muted-foreground">—</span>;
  }

  const visible = entities.slice(0, 2).join(", ");
  const remaining = entities.length - 2;
  const suffix = remaining > 0 ? "..." : "";

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="block cursor-default truncate text-sm text-[#414651] dark:text-foreground">
            {visible}
            {suffix}
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs rounded-lg bg-[#0C111D] px-3 py-2 text-sm font-medium text-white shadow-lg dark:bg-white dark:text-[#0C111D]">
          {entities.join(", ")}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function AlertStatusBadge({ state }: { state: string }) {
  const styles: Record<string, string> = {
    Open: "border-[#B2DDFF] bg-[#EFF8FF] text-[#175CD3] dark:border-sky-400/30 dark:bg-sky-500/15 dark:text-sky-300",
    New: "border-[#B2DDFF] bg-[#EFF8FF] text-[#175CD3] dark:border-sky-400/30 dark:bg-sky-500/15 dark:text-sky-300",
    Assigned: "border-[#B2DDFF] bg-[#EFF8FF] text-[#175CD3] dark:border-sky-400/30 dark:bg-sky-500/15 dark:text-sky-300",
    Reviewed: "border-[#B2DDFF] bg-[#EFF8FF] text-[#175CD3] dark:border-sky-400/30 dark:bg-sky-500/15 dark:text-sky-300",
    Resolved: "border-[#ABEFC6] bg-[#ECFDF3] text-[#067647] dark:border-emerald-400/30 dark:bg-emerald-500/15 dark:text-emerald-300",
    Ignored: "border-[#E9EAEB] dark:border-border bg-[#FAFAFA] dark:bg-muted/40 text-[#414651] dark:text-foreground",
  };

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium",
        styles[state] ?? "border-[#E9EAEB] dark:border-border bg-[#FAFAFA] dark:bg-muted/40 text-[#414651] dark:text-foreground",
      )}
    >
      {state}
    </span>
  );
}

function RouteComponent() {
  const navigate = Route.useNavigate();
  const searchParams = Route.useSearch();
  const [searchQuery, setSearchQuery] = useState(searchParams.search ?? "");
  const [alertToReview, setAlertToReview] = useState<AlertItem | null>(null);
  const [alertToDelete, setAlertToDelete] = useState<AlertItem | null>(null);

  const stateFilters: Array<AlertStateFilter> = searchParams.state;
  const typeFilters: Array<AlertTypeFilter> = searchParams.alert_type;
  const dateFilter = searchParams.date_filter ?? "all";

  const alerts = useQuery(
    alertsListQueryOptions({
      ...searchParams,
      ...getAlertDateRange(dateFilter),
      search: searchQuery,
      state: toApiFilterValue(stateFilters, "All"),
      alert_type: toApiFilterValue(typeFilters, "all"),
    }),
  );

  const items = (alerts.data?.items ?? []).filter(
    (alert) =>
      matchesAlertState(alert, stateFilters) &&
      matchesAlertType(alert, typeFilters),
  );

  const table = useReactTable({
    data: items,
    columns: [],
    state: {
      pagination: {
        pageIndex: searchParams.page - 1,
        pageSize: searchParams.per_page,
      },
    },
    rowCount: alerts.data?.count ?? 0,
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const handleApplyFilters = (
    nextState: Array<AlertStateFilter>,
    nextType: Array<AlertTypeFilter>,
  ) => {
    navigate({
      to: ".",
      search: (prev) => ({
        ...prev,
        page: 1,
        state: nextState,
        alert_type: nextType,
      }),
    });
  };

  const handleDateFilterChange = (value: string) => {
    navigate({
      to: ".",
      search: (prev) => ({
        ...prev,
        page: 1,
        date_filter: value === "all" ? undefined : value,
      }),
    });
  };

  const handleSearchQueryChange = (value: string) => {
    setSearchQuery(value);

    if (searchParams.page !== 1) {
      navigate({
        to: ".",
        search: (prev) => ({ ...prev, page: 1 }),
      });
    }
  };

  return (
    <div className="flex w-full flex-col gap-6">
      {/* Mobile header */}
      <div className="flex flex-col gap-6 md:hidden">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-lg leading-7 font-semibold text-[#181D27] dark:text-foreground">
            Live Monitors &amp; Alerts
          </h1>
          <div className="flex items-center gap-3">
            <AlertsDateFilterButton
              value={dateFilter}
              onChange={handleDateFilterChange}
            />
            <AlertsFilterPopover
              stateFilters={stateFilters}
              typeFilters={typeFilters}
              onApply={handleApplyFilters}
            />
          </div>
        </div>

        <div className="relative w-full">
          <Search className="absolute top-1/2 left-3 size-5 -translate-y-1/2 text-[#717680] dark:text-muted-foreground" />
          <Input
            placeholder="Search..."
            value={searchQuery}
            onChange={(event) => handleSearchQueryChange(event.target.value)}
            className="h-10 pl-10 text-sm shadow-xs"
          />
        </div>
      </div>

      {/* Desktop header */}
      <div className="hidden items-center justify-between gap-4 md:flex">
        <h1 className="shrink-0 text-2xl leading-8 font-semibold text-[#181D27] dark:text-foreground">
          Live Monitors &amp; Alerts
        </h1>

        <div className="flex w-[535px] shrink-0 items-center gap-4 xl:w-auto xl:max-w-3xl">
          <div className="relative min-w-0 flex-1">
            <Search className="absolute top-1/2 left-3 size-5 -translate-y-1/2 text-[#717680] dark:text-muted-foreground" />
            <Input
              placeholder="Search..."
              value={searchQuery}
              onChange={(event) => handleSearchQueryChange(event.target.value)}
              className="h-10 pl-10 text-sm shadow-xs"
            />
          </div>

          <div className="flex shrink-0 items-center gap-3">
            <AlertsDateFilterButton
              value={dateFilter}
              onChange={handleDateFilterChange}
            />
            <AlertsFilterPopover
              stateFilters={stateFilters}
              typeFilters={typeFilters}
              onApply={handleApplyFilters}
            />
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-[10px] border border-[#E9EAEB] dark:border-border bg-card shadow-xs">
        <Table className="min-w-[1100px] md:table-fixed">
          <TableHeader className="bg-[#FAFAFA] dark:bg-muted/40 [&_tr]:border-b [&_tr]:border-[#E9EAEB] dark:border-border">
            <TableRow className="hover:bg-transparent">
              <TableHead className={tableHeadClass}>Meeting</TableHead>
              <TableHead className={tableHeadClass}>Date &amp; Time</TableHead>
              <TableHead className={tableHeadClass}>Participant</TableHead>
              <TableHead className={tableHeadClass}>Threats</TableHead>
              <TableHead className={cn(tableHeadClass, "md:max-w-[220px]")}>
                Entities
              </TableHead>
              <TableHead className={cn(tableHeadClass, "md:w-[120px]")}>
                Status
              </TableHead>
              <TableHead
                className={cn(tableHeadClass, "text-right md:w-[88px]")}
              >
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {alerts.isLoading ? (
              [...Array(searchParams.per_page)].map((_, index) => (
                <TableRow
                  key={index}
                  className="border-b border-[#E9EAEB] dark:border-border last:border-b-0 hover:bg-transparent"
                >
                  <TableCell colSpan={7} className="px-4 py-4 md:px-6">
                    <Skeleton className="h-10 w-full" />
                  </TableCell>
                </TableRow>
              ))
            ) : !alerts.isSuccess ? (
              <TableRow className="hover:bg-transparent">
                <TableCell
                  colSpan={7}
                  className="px-4 py-16 text-center text-muted-foreground md:px-6"
                >
                  Failed to load alerts
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow className="hover:bg-transparent">
                <TableCell
                  colSpan={7}
                  className="px-4 py-16 text-center text-muted-foreground md:px-6"
                >
                  No alerts found
                </TableCell>
              </TableRow>
            ) : (
              items.map((alert) => {
                return (
                  <TableRow
                    key={alert.id}
                    className="border-b border-[#E9EAEB] dark:border-border last:border-b-0 hover:bg-transparent"
                  >
                    <MeetingCell alert={alert} searchQuery={searchQuery} />
                    <TableCell
                      className={cn(
                        tableCellClass,
                        "text-sm whitespace-nowrap text-[#535862] dark:text-muted-foreground",
                      )}
                    >
                      <span
                        className="block truncate"
                        title={formatAlertDate(alert.detected_at)}
                      >
                        {formatAlertDate(alert.detected_at)}
                      </span>
                    </TableCell>
                    <TableCell className="p-0">
                      <ParticipantCell
                        participant={alert.participant}
                        searchQuery={searchQuery}
                      />
                    </TableCell>
                    <TableCell className={tableCellClass}>
                      <ThreatsCell alert={alert} />
                    </TableCell>
                    <TableCell
                      className={cn(tableCellClass, "max-w-0 overflow-hidden")}
                    >
                      <EntitiesCell alert={alert} />
                    </TableCell>
                    <TableCell className={cn(tableCellClass, "md:w-[120px]")}>
                      <AlertStatusBadge state={alert.state} />
                    </TableCell>
                    <TableCell
                      className={cn(tableCellClass, "text-right md:w-[88px]")}
                    >
                      <AlertActionsMenu
                        onReview={() => setAlertToReview(alert)}
                        onGoToMeeting={() =>
                          navigate({
                            to: "/meetings/$meetingId",
                            params: { meetingId: alert.meeting.id },
                          })
                        }
                        onDelete={() => setAlertToDelete(alert)}
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        <MeetingsPagination
          table={table}
          totalCount={alerts.data?.count ?? 0}
        />
      </div>

      <ReviewDialog
        alert={alertToReview}
        open={alertToReview !== null}
        onOpenChange={(open) => {
          if (!open) setAlertToReview(null);
        }}
      />

      <DeleteDialog
        alert={alertToDelete}
        open={alertToDelete !== null}
        onOpenChange={(open) => {
          if (!open) setAlertToDelete(null);
        }}
      />
    </div>
  );
}

const alertQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ["alerts", "detail", id],
    queryFn() {
      return api.processing.raw.get(`api/v1/alerts/${id}`).json<AlertDetail>();
    },
  });
const alertTypeToFeatureMap = {
  pii_exposure: "pii",
  voice_authentication_failure: "voice_authentication",
  ai_content_detected: "ai_content_detection",
} as const;

function ReviewDialog({
  alert,
  open,
  onOpenChange,
}: {
  alert: AlertItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [resolutionNote, setResolutionNote] = React.useState("");

  React.useEffect(() => {
    if (!open) setResolutionNote("");
  }, [open]);

  const detailQuery = useQuery({
    ...(alert ? alertQueryOptions(alert.id) : alertQueryOptions("__none__")),
    enabled: open && !!alert,
  });
  const detail = detailQuery.data;

  const enabledFeatures = new Set(
    detailQuery.data?.alert_types.map((type) => alertTypeToFeatureMap[type]),
  );

  const utterance = useQuery({
    ...utteranceDetailsQueryOptions(alert?.utterance_id ?? "__none__"),
    enabled: open && !!alert,
    select(entry) {
      const utteranceId = entry.id;

      const meetingStart = new Date(entry.meeting.meeting_start_time);
      const startTime = new Date(entry.start_time).getTime();
      const endTime = addMilliseconds(startTime, entry.duration_ms);

      const utterance: Utterance = {
        id: utteranceId,
        speaker: {
          id: entry.participant.id,
          name: entry.participant.full_name,
        },

        start: duration.between(meetingStart, startTime),
        end: duration.between(meetingStart, endTime),
        duration: duration.between(startTime, endTime),

        text: entry.transcription_text,
        transcriptionWords: {
          status: "success",
          data: entry.transcription_words.map((transcription) => {
            return {
              word: transcription.word,
              start: duration.between(
                meetingStart,
                addSeconds(startTime, transcription.start),
              ),
              end: duration.between(
                meetingStart,
                addSeconds(startTime, transcription.end),
              ),
            };
          }),
          error: null,
        },

        ai_detection: { status: "null", data: null, error: null },
        pii: { status: "null", data: null, error: null },
        vexon: { status: "null", data: null, error: null },
      };
      if (!utterance.duration.minutes) utterance.duration.minutes = 0;

      if (entry.ai_results.pii?.result_type === "success")
        utterance.pii = {
          status: "success",
          data: entry.ai_results.pii.entities.toSorted(),
          error: null,
        };
      if (entry.ai_results.ai_content_detection?.result_type === "success")
        utterance.ai_detection = {
          status: "success",
          data: entry.ai_results.ai_content_detection,
          error: null,
        };

      if (entry.ai_results.voice_authentication?.result_type === "success")
        utterance.vexon = {
          status: "success",
          data: {
            fakeRatio:
              entry.ai_results.voice_authentication.overall_score.authenticity
                .fake_ratio,
            chunks: entry.ai_results.voice_authentication.results.map(
              (chunk) => ({
                startTime: chunk.start_time,
                endTime: chunk.end_time,
                isFake: chunk.authenticity.result === "Fake Voice",
              }),
            ),
          },
          error: null,
        };

      return utterance;
    },
  });

  const isLoading = detailQuery.isLoading || utterance.isLoading;
  const isError = detailQuery.isError || utterance.isError;
  const isSuccess = detailQuery.isSuccess && utterance.isSuccess;

  const queryClient = useQueryClient();
  const updateAlertMutation = useMutation({
    mutationFn: async ({
      alertId,
      payload,
    }: {
      alertId: string;
      payload: UpdateAlertPayload;
    }) => {
      await api.processing.raw.put(`api/v1/alerts/${alertId}`, {
        json: payload,
      });
    },
    onSuccess: async () => {
      toast.success("Alert updated successfully!");
      onOpenChange(false);
      setResolutionNote("");
      await queryClient.invalidateQueries({ queryKey: ["alerts"] });
    },
    async onError(error) {
      if (error instanceof HTTPError) {
        try {
          const body = await error.response.json();
          if (body.detail) {
            toast.error(body.detail);
            return;
          }
        } catch {
          // ignore json parse errors
        }
      }
      toast.error("Failed to update alert");
    },
  });

  const meetingQuery = useQuery({
    ...meetingBotQueryOptions(alert?.meeting.id ?? "__none__"),
    enabled: open && !!alert,
  });

  if (!alert) return null;

  const displayAlert = detail ?? alert;
  const alertTypes = displayAlert.alert_types ?? [];
  const entities = getAlertEntities(displayAlert);
  const meetingUrl = meetingQuery.data?.meeting_url ?? null;
  const participantName =
    detail?.participant_full_name ?? alert.participant?.name ?? "—";
  const meetingName = detail ? detail.meeting_name : alert.meeting.name;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="grid max-h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] grid-cols-[minmax(0,1fr)] gap-0 overflow-x-hidden overflow-y-auto p-0 sm:w-full sm:max-w-[640px]">
        <AlertDialogHeader className="min-w-0 gap-0 space-y-0 px-6 pt-6 pb-4 text-left">
          <div className="flex min-w-0 items-start justify-between gap-4">
            <AlertDialogTitle className="min-w-0 truncate text-xl font-semibold text-[#181D27] dark:text-foreground">
              Incident Review
            </AlertDialogTitle>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="-mt-1 -mr-1 inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-[#717680] dark:text-muted-foreground transition-colors hover:bg-muted/60 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <AlertDialogDescription asChild>
            <span className="sr-only">Review and resolve this alert</span>
          </AlertDialogDescription>
        </AlertDialogHeader>

        {isLoading && (
          <div className="flex min-h-[280px] flex-col items-center justify-center px-6 pb-6">
            <LoadingSpinner size={40} />
            <p className="mt-4 text-sm text-muted-foreground">
              Loading alert details...
            </p>
          </div>
        )}

        {isError && (
          <>
            <div className="mx-6 rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
              Failed to load alert or utterance details. Please try again.
            </div>
            <AlertDialogFooter className="flex min-w-0 flex-col-reverse gap-2 px-6 py-4 sm:flex-row sm:justify-end">
              <AlertDialogCancel
                disabled={updateAlertMutation.isPending}
                asChild
              >
                <Button variant="outline" className="w-full min-w-0 sm:w-auto">
                  Close
                </Button>
              </AlertDialogCancel>
            </AlertDialogFooter>
          </>
        )}

        {isSuccess && (
          <>
            <div className="flex min-w-0 flex-col gap-5 px-6 pb-2">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="truncate text-base font-semibold text-[#181D27] dark:text-foreground">
                    {meetingName}
                  </p>
                  <p className="mt-0.5 text-xs text-[#717680] dark:text-muted-foreground">
                    {formatAlertDate(displayAlert.detected_at)}
                  </p>
                </div>
                <AlertStatusBadge state={displayAlert.state} />
              </div>

              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div className="min-w-0">
                  <p className="text-xs font-medium text-[#717680] dark:text-muted-foreground">
                    Alert type
                  </p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {alertTypes.length === 0 ? (
                      <span className="text-sm text-[#535862] dark:text-muted-foreground">—</span>
                    ) : (
                      alertTypes.map((type) => (
                        <Badge
                          key={type}
                          variant="outline"
                          className={cn(
                            "rounded-full px-2.5 py-0.5 text-xs font-medium",
                            getAlertTypeBadgeClasses(type),
                          )}
                        >
                          {formatAlertTypeBadge(type)}
                        </Badge>
                      ))
                    )}
                  </div>
                </div>

                <div className="min-w-0">
                  <p className="text-xs font-medium text-[#717680] dark:text-muted-foreground">
                    Participant
                  </p>
                  <div className="mt-2 flex min-w-0 items-center gap-2">
                    {/* <Avatar className="h-7 w-7 shrink-0">
                      <AvatarFallback
                        className={cn(
                          "text-[10px] font-medium",
                          getAvatarColor(participantName),
                        )}
                      >
                        {getInitials(participantName)}
                      </AvatarFallback>
                    </Avatar> */}
                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-[#181D27] dark:text-foreground">
                      {participantName}
                    </span>
                  </div>
                </div>
              </div>

              <div className="min-w-0">
                <p className="text-xs font-medium text-[#717680] dark:text-muted-foreground">Entities</p>
                <p className="mt-1.5 text-sm wrap-break-word text-[#181D27] dark:text-foreground">
                  {entities.length === 0 ? "—" : entities.join(", ")}
                </p>
              </div>

              <div className="min-w-0">
                <p className="text-xs font-medium text-[#717680] dark:text-muted-foreground">
                  Meeting URL
                </p>
                <div className="mt-1.5 flex min-w-0 items-center gap-2">
                  {meetingUrl ? (
                    <>
                      <a
                        href={meetingUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="block min-w-0 truncate text-sm font-medium text-[#181D27] dark:text-foreground hover:underline"
                      >
                        {meetingUrl}
                      </a>
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(meetingUrl);
                            toast.success("Meeting URL copied to clipboard");
                          } catch {
                            toast.error("Failed to copy meeting URL");
                          }
                        }}
                        className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-[#717680] dark:text-muted-foreground transition-colors hover:bg-muted/60 hover:text-[#414651]"
                        aria-label="Copy meeting URL"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                    </>
                  ) : (
                    <span className="text-sm text-[#535862] dark:text-muted-foreground">—</span>
                  )}
                </div>
              </div>

              <TranscriptionAnalysisSection
                utterance={utterance.data}
                enabledFeatures={enabledFeatures}
              />

              <div className="space-y-1.5">
                <label
                  htmlFor="resolution-note"
                  className="text-xs font-medium text-[#717680] dark:text-muted-foreground"
                >
                  Resolution note
                </label>
                <Textarea
                  id="resolution-note"
                  placeholder="Add details about the investigation or resolution..."
                  value={resolutionNote}
                  onChange={(e) => setResolutionNote(e.target.value)}
                  className="min-h-24 resize-y text-sm"
                />
              </div>
            </div>

            <AlertDialogFooter className="flex min-w-0 flex-col-reverse gap-2 px-6 py-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
              <AlertDialogCancel
                disabled={updateAlertMutation.isPending}
                asChild
              >
                <Button
                  variant="ghost"
                  className="w-full min-w-0 border-0 text-[#535862] dark:text-white shadow-none hover:bg-transparent hover:text-[#181D27] dark:hover:text-white sm:w-auto"
                >
                  Cancel
                </Button>
              </AlertDialogCancel>
              <Button
                variant="outline"
                className="w-full min-w-0 sm:w-auto"
                disabled={
                  updateAlertMutation.isPending ||
                  displayAlert.state === ALERT_STATE.IGNORED
                }
                onClick={() => {
                  updateAlertMutation.mutate({
                    alertId: alert.id,
                    payload: {
                      state: ALERT_STATE.IGNORED,
                      resolution_note: resolutionNote || undefined,
                    },
                  });
                }}
              >
                Mark as ignored
              </Button>
              <Button
                className="w-full min-w-0 bg-[#D6298E] text-white hover:bg-[#BC2079] sm:w-auto"
                disabled={
                  updateAlertMutation.isPending ||
                  displayAlert.state === ALERT_STATE.RESOLVED
                }
                onClick={() => {
                  updateAlertMutation.mutate({
                    alertId: alert.id,
                    payload: {
                      state: ALERT_STATE.RESOLVED,
                      resolution_note: resolutionNote || undefined,
                    },
                  });
                }}
              >
                Resolve Incident
              </Button>
            </AlertDialogFooter>
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}

function TranscriptionAnalysisSection({
  utterance,
  enabledFeatures,
}: {
  utterance: Utterance;
  enabledFeatures: Set<"ai_content_detection" | "voice_authentication" | "pii">;
}) {
  const { tabs, activeTab, setActiveTab } = useUtteranceAnalysisTabs(
    utterance,
    enabledFeatures,
  );

  return (
    <div className="min-w-0 space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs font-medium text-[#717680] dark:text-muted-foreground">
          Transcription Analysis
        </p>
        {tabs.length > 0 && (
          <UtteranceAnalysisTabBar
            tabs={tabs}
            activeTab={activeTab}
            onChange={setActiveTab}
            variant="timeline"
          />
        )}
      </div>

      <UtteranceAnalysisTabContent
        utterance={utterance}
        enabledFeatures={enabledFeatures}
        activeTab={activeTab}
      />
    </div>
  );
}

function DeleteDialog({
  alert,
  open,
  onOpenChange,
}: {
  alert: AlertItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn(id: string) {
      return api.processing.raw.delete(`api/v1/alerts/${id}`);
    },
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ["alerts", "list"] });
      if (alert) {
        queryClient.removeQueries({ queryKey: ["alerts", "detail", alert.id] });
      }
      onOpenChange(false);
      toast.success(`Alert deleted successfully`);
    },
    async onError(err) {
      let message = err.message;
      if (err instanceof HTTPError) {
        const body = await err.response.json().catch(() => null);
        if (body?.detail) message = body.detail;
      }
      toast.error(`Delete failed: ${message}`);
    },
  });

  return (
    <ConfirmationDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Delete alert?"
      description="Are you sure you want to delete the alert? This action cannot be undone."
      onConfirm={() => {
        if (alert) deleteMutation.mutate(alert.id);
      }}
      confirmText="Delete"
      cancelText="Cancel"
      variant="destructive"
      isLoading={deleteMutation.isPending}
      icon={ConfirmationDialogWarningIcon}
      backgroundDecoration
      emphasizeCancel
    />
  );
}
